/**
 * L'état de l'application, et son mécanisme d'abonnement.
 *
 * Volontairement minuscule : un objet, une fonction de mise à jour, des
 * abonnés. Une bibliothèque de gestion d'état n'apporterait rien ici, et
 * pèserait plus lourd que tout le reste du code.
 */
import { FALLBACK_POSITION } from '../core/constants';
import type {
  AirSnapshot,
  BurntArea,
  FireZone,
  NoseEntry,
  ScanReport,
  SensorReading,
  UserPosition,
  WeatherSnapshot,
} from '../core/types';

export interface ScanProgressState {
  done: number;
  total: number;
}

export interface AppState {
  position: UserPosition;
  air: AirSnapshot | null;
  weather: WeatherSnapshot | null;
  sensors: readonly SensorReading[];
  nose: readonly NoseEntry[];
  zones: readonly FireZone[];
  /**
   * Surfaces brûlées récentes, pour le contexte de la carte.
   *
   * Volontairement séparées de `zones` : elles ne participent à aucun verdict.
   * Ce qui a brûlé ne fume plus. Voir src/core/burnt.ts.
   */
  burnt: readonly BurntArea[];
  /** Décalage en jours du dernier passage satellite exploitable. */
  fireDay: number;
  scanning: boolean;
  scanReport: ScanReport;
  scanProgress: ScanProgressState | null;
  /** Instant du dernier rafraîchissement réussi des données locales. */
  lastRefresh: number | null;
  /** Message temporaire affiché sous la barre de recherche. */
  message: string | null;
}

export const initialState: AppState = {
  position: { ...FALLBACK_POSITION },
  air: null,
  weather: null,
  sensors: [],
  nose: [],
  zones: [],
  burnt: [],
  fireDay: -1,
  scanning: false,
  scanReport: { ok: 0, failed: 0, error: null },
  scanProgress: null,
  lastRefresh: null,
  message: null,
};

export type Listener = (state: AppState) => void;

export interface Store {
  get(): AppState;
  /** Applique une modification partielle et prévient les abonnés. */
  patch(changes: Partial<AppState>): void;
  subscribe(listener: Listener): () => void;
}

export function createStore(seed: AppState = initialState): Store {
  let state = seed;
  const listeners = new Set<Listener>();

  return {
    get: () => state,

    patch(changes) {
      state = { ...state, ...changes };
      for (const listener of listeners) {
        // Un abonné qui échoue ne doit pas empêcher les autres de se mettre
        // à jour, ni faire tomber la boucle de rafraîchissement.
        try {
          listener(state);
        } catch (error) {
          console.error('Abonné en erreur', error);
        }
      }
    },

    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}
